import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import ApplicationForm from '../application/ApplicationForm';
import { 
  submitEventApplication, 
  ApplicationSubmissionData,
  checkUserEventApplication 
} from '../../services/applicationService';
import { getEventById } from '../../services/eventService';
import { EventItem } from '../../types/event';
import { logger } from '../../utils/logger';
import PageHeader from '../common/PageHeader';
import EventDetailCard from '../common/EventDetailCard';
import { useAuth } from '../../context/AuthContext';
import { useAnalytics, useScrollTracking } from '../../hooks/useAnalytics';
import VerificationModal from '../auth/VerificationModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faSpinner } from '@fortawesome/free-solid-svg-icons';
import '../../styles/Apply.css';
import '../../styles/VerificationModal.css';

// Added styles for the "How It Works" section
const howItWorksStyles = `
  .journey-section {
    margin-bottom: 2rem;
  }
  
  .journey-steps {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 1.5rem;
    margin-top: 1.5rem;
  }
  
  .step {
    background-color: rgba(156, 39, 176, 0.1);
    border-left: 3px solid var(--accent-color);
    padding: 1.5rem;
    border-radius: 8px;
    transition: all 0.3s ease;
  }
  
  .step:hover {
    transform: translateY(-5px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  }
  
  .step h3 {
    color: var(--accent-color);
    margin-bottom: 0.75rem;
    font-size: 1.2rem;
  }
  
  .faq-section {
    margin-bottom: 2rem;
  }
  
  .faq-accordion {
    margin-top: 1rem;
  }
  
  .faq-item {
    margin-bottom: 0.5rem;
    border-radius: 8px;
    overflow: hidden;
  }
  
  .faq-question {
    background-color: rgba(156, 39, 176, 0.1);
    padding: 1rem;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    transition: background-color 0.3s ease;
  }
  
  .faq-question:hover {
    background-color: rgba(156, 39, 176, 0.2);
  }
  
  .faq-question span {
    font-weight: 600;
  }
  
  .faq-question .icon {
    font-weight: bold;
    font-size: 1.2rem;
  }
  
  .faq-answer {
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.3s ease;
    background-color: rgba(30, 30, 30, 0.8);
  }
  
  .faq-answer.active {
    max-height: 300px;
    padding: 1rem;
  }
  
  .mt-5 {
    margin-top: 3rem;
  }
  
  .mb-3 {
    margin-bottom: 1.5rem;
  }
  
  .mb-4 {
    margin-bottom: 2rem;
  }
`;

const Apply: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [applicationId, setApplicationId] = useState<string>('');
  const [trackingUrl, setTrackingUrl] = useState<string>('');
  const [searchParams] = useSearchParams();
  const [eventId, setEventId] = useState<string | null>(null);
  const [eventIdValid, setEventIdValid] = useState<boolean | null>(null);
  const [eventDetails, setEventDetails] = useState<EventItem | null>(null);
  const [fetchingEvent, setFetchingEvent] = useState<boolean>(false);
  const [hasApplied, setHasApplied] = useState<boolean>(false);
  const [existingApplicationId, setExistingApplicationId] = useState<string | undefined>(undefined);
  const [applicationStatus, setApplicationStatus] = useState<string | undefined>(undefined);
  const [checkingApplication, setCheckingApplication] = useState<boolean>(false);
  const [showVerificationModal, setShowVerificationModal] = useState<boolean>(false);
  const [verificationSending, setVerificationSending] = useState<boolean>(false);
  const [verificationCooldown, setVerificationCooldown] = useState<number>(0);
  const contentSectionRef = useRef<HTMLElement | null>(null);
  const navigate = useNavigate();
  const { currentUser, isEmailVerified, verifyEmail, watchEmailVerificationStatus } = useAuth();
  const { trackPageView, trackCustomEvent } = useAnalytics();
  useScrollTracking(); // Automatic scroll tracking

  const handleSubmit = async (formData: ApplicationSubmissionData, photoFile?: File) => {
    setLoading(true);
    setError(null);
    
    try {
      logger.info('📝 Handling application form submission', { eventId: formData.eventId });
      
      // The eventId is already included in the formData from the ApplicationForm component
      const response = await submitEventApplication(formData, photoFile);
      logger.success('✅ Application submitted successfully');
      setSuccess(true);
      setApplicationId(response.applicationId);
      setTrackingUrl(response.trackingUrl);
      
      // Track successful application submission
      trackCustomEvent('application_submitted', {
        event_id: formData.eventId,
        application_id: response.applicationId,
        page: 'apply',
        has_photo: !!photoFile
      });
      
      window.scrollTo(0, 0);
    } catch (err) {
      logger.error('❌ Error submitting application:', err);
      
      // Handle specific error types with user-friendly messages
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      
      // Provide more specific error messages for common errors
      if (errorMessage.includes('Authentication required')) {
        setError('You need to be logged in to submit an application. Please log in and try again.');
        
        // Track specific authentication error
        trackCustomEvent('application_auth_error', {
          event_id: formData.eventId,
          page: 'apply',
          error_type: 'authentication_required'
        });
      } else if (errorMessage.includes('network') || errorMessage.includes('Failed to fetch')) {
        setError('Network error. Please check your internet connection and try again.');
        
        // Track network error
        trackCustomEvent('application_network_error', {
          event_id: formData.eventId,
          page: 'apply'
        });
      } else {
        // General error
        setError(errorMessage);
        
        // Track general application submission error
        trackCustomEvent('application_submission_error', {
          event_id: formData.eventId,
          page: 'apply',
          error_message: errorMessage
        });
      }
      
      window.scrollTo(0, 0);
    } finally {
      setLoading(false);
    }
  };

  const handleViewStatus = () => {
    trackCustomEvent('application_status_clicked', {
      event_id: eventId || '',
      application_id: applicationId || '',
      page: 'apply'
    });
    navigate(`/eventapplication/${applicationId}`);
  };

  const handleViewExistingApplication = () => {
    trackCustomEvent('existing_application_viewed', {
      event_id: eventId || '',
      application_id: existingApplicationId || '',
      page: 'apply'
    });
    if (existingApplicationId) {
      navigate(`/eventapplication/${existingApplicationId}`);
    }
  };

  const checkFade = () => {
    if (contentSectionRef.current) {
      const elementTop = contentSectionRef.current.getBoundingClientRect().top;
      const windowHeight = window.innerHeight;
      if (elementTop < windowHeight - 100) {
        contentSectionRef.current.classList.add('active');
      }
    }
  };

  useEffect(() => {
    checkFade();
    window.addEventListener('scroll', checkFade);
    
    return () => {
      window.removeEventListener('scroll', checkFade);
    };
  }, []);
  
  // Effect to track page view when component mounts
  useEffect(() => {
    // Track page view
    trackPageView({ 
      page_title: 'Event Application Form',
      page_location: window.location.href 
    });

    // Track form view
    trackCustomEvent('application_form_viewed', {
      event_id: eventId || 'unknown',
      page: 'apply',
      logged_in: !!currentUser
    });
  }, [trackPageView, trackCustomEvent, eventId, currentUser]);

  // Effect to check and validate the eventId from URL parameters
  useEffect(() => {
    const urlEventId = searchParams.get('eventId');
    if (urlEventId && urlEventId.trim().length > 0) {
      setEventId(urlEventId);
      logger.info('🔍 Event ID found in URL parameters', { eventId: urlEventId });
      
      // Fetch event details
      setFetchingEvent(true);
      getEventById(urlEventId)
        .then(event => {
          if (event) {
            setEventDetails(event);
            setEventIdValid(true);
            logger.success('✅ Event details fetched successfully', { eventId: urlEventId, title: event.title });
          } else {
            setEventIdValid(false);
            logger.error('❌ Invalid event ID - event not found', { eventId: urlEventId });
          }
        })
        .catch(err => {
          setEventIdValid(false);
          logger.error('❌ Error fetching event details:', err);
        })
        .finally(() => {
          setFetchingEvent(false);
        });
    } else {
      logger.error('❌ Missing or empty event ID in URL parameters');
      setEventId(null);
      setEventIdValid(false);
    }
  }, [searchParams]);

  // Check if the user has already applied for this event
  useEffect(() => {
    if (!eventId || !currentUser) return;
    
    const checkApplicationStatus = async () => {
      setCheckingApplication(true);
      try {
        logger.info('🔍 Checking if user has already applied', { eventId });
        const result = await checkUserEventApplication(eventId);
        
        setHasApplied(result.hasApplied);
        setExistingApplicationId(result.applicationId);
        setApplicationStatus(result.status);
        
        logger.info('✅ Application status check completed', { 
          hasApplied: result.hasApplied,
          applicationId: result.applicationId,
          status: result.status
        });
        
        // If user has already applied, set error to prevent duplicate submissions
        if (result.hasApplied) {
          setError('You have already applied for this event. You can check your application status below.');
        }
      } catch (error) {
        logger.error('❌ Error checking application status:', error);
        // Don't set an error state here to avoid disrupting the UI
      } finally {
        setCheckingApplication(false);
      }
    };
    
    checkApplicationStatus();
  }, [eventId, currentUser]);

  // Handle sending verification email
  const handleSendVerificationEmail = async () => {
    if (!currentUser) return;
    
    trackCustomEvent('verification_email_requested', {
      source: 'apply_page'
    });
    
    setVerificationSending(true);
    try {
      await verifyEmail();
      setError('Verification email sent! Please check your inbox.');
      
      // Set timestamp in localStorage
      localStorage.setItem('verificationEmailSent', Date.now().toString());
      setVerificationCooldown(60); // 60 seconds cooldown
      
      // Track success
      trackCustomEvent('verification_email_sent', {
        source: 'apply_page'
      });
      
      logger.info('📧 Verification email sent from application page');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to send verification email: ${errorMsg}`);
      
      // Track error
      trackCustomEvent('verification_email_error', {
        error_message: errorMsg,
        source: 'apply_page'
      });
      
      logger.error('❌ Error sending verification email', err);
    } finally {
      setVerificationSending(false);
      setShowVerificationModal(false);
    }
  };

  // Check localStorage for existing countdown on component mount
  useEffect(() => {
    const emailSentTimestamp = localStorage.getItem('verificationEmailSent');
    if (emailSentTimestamp) {
      const elapsed = Math.floor((Date.now() - parseInt(emailSentTimestamp)) / 1000);
      const cooldown = 60; // 60 seconds cooldown
      
      if (elapsed < cooldown) {
        setVerificationCooldown(cooldown - elapsed);
      }
    }
  }, []);

  // Countdown timer effect
  useEffect(() => {
    if (verificationCooldown <= 0) return;
    
    const timer = setInterval(() => {
      setVerificationCooldown(prev => Math.max(0, prev - 1));
    }, 1000);
    
    return () => clearInterval(timer);
  }, [verificationCooldown]);

  // Add useEffect to check for verification status before form submission
  useEffect(() => {
    if (currentUser && !isEmailVerified) {
      logger.info('⚠️ Unverified user attempting to access application form');
    }
  }, [currentUser, isEmailVerified]);

  // Watch for email verification status changes
  useEffect(() => {
    if (!currentUser || isEmailVerified) return;
    
    // Start watching for verification status changes
    const stopWatching = watchEmailVerificationStatus((verified) => {
      if (verified) {
        // User's email was just verified
        logger.info('✅ Email verification detected while on application page');
        
        // Show success message
        setError('Email verified successfully! You can now submit your application.');
        
        // Track this event
        trackCustomEvent('email_verified_during_application', {
          event_id: eventId || '',
          source: 'apply_page'
        });
      }
    });
    
    // Clean up the watcher when unmounting
    return () => stopWatching();
  }, [currentUser, isEmailVerified, watchEmailVerificationStatus, trackCustomEvent, eventId]);

  return (
    <>
      <style>{howItWorksStyles}</style>

      <PageHeader
        title="Apply"
        subtitle="Join our community by completing the application process"
      />
      <section className="container fade-in" ref={contentSectionRef}>
        {success ? (
          <div className="application-success">
            <div className="success-icon">✓</div>
            <h2>Application Submitted Successfully!</h2>
            <p>Thank you for applying to our BodySharing event. Your application has been received and will be reviewed by our team.</p>
            <div className="tracking-info">
            <p>You can track the status of your application using the link below:</p>
            <div className="tracking-url">
              <label htmlFor="tracking-url" className="sr-only">Application tracking URL</label>
              <input 
                type="text" 
                id="tracking-url"
                value={trackingUrl} 
                readOnly 
                aria-label="Application tracking URL"
                title="Application tracking URL"
                placeholder="Your application tracking URL"
              />
              <button className="copy-button" onClick={() => {
                navigator.clipboard.writeText(trackingUrl);
                alert('Tracking URL copied to clipboard!');
              }}>Copy</button>
            </div>
              <p className="tracking-note"><strong>Note:</strong> Please save this link as it allows you to check your application status.</p>
            </div>
            <div className="success-actions">
              <button onClick={handleViewStatus} className="primary-button">View Application Status</button>
            </div>
          </div>
        ) : (
          <>
            {(!eventId || !eventIdValid) ? (
              <div className="error-message">
                <p><strong>Missing or Invalid Event ID:</strong> A valid event ID is required to submit an application.</p>
                <p>Please access this page through the event link on our website.</p>
                <Link to="/" className="primary-button" style={{ display: 'inline-block', marginTop: '1rem' }}>
                  Go to Events
                </Link>
              </div>
            ) : (
              <>
                <div className="journey-section">
                  <h2 className="text-center">How It Works</h2>
                </div>
                
                <div className="apply-intro mt-5">
                  <div className="application-notice">
                    <h3>Application Process</h3>
                  {eventDetails && !fetchingEvent && (
                    <EventDetailCard event={eventDetails} />
                  )}
                  {fetchingEvent && (                    <div className="loading-event">
                      <p>Loading event details...</p>
                    </div>
                  )}
                  <ol>
                    <li>Read <Link to="/consent" className="inline-link">consent & guidelines</Link></li>
                    <li>Complete the application form below (if you come as couple you need to apply both separately, and write who your partner is)</li>
                    <li>You'll receive a confirmation email with a tracking link</li>
                    <li>Our team will review your application</li>
                    <li>You'll be notified of the decision via email</li>
                    <li>If approved, you'll receive an entry QR code and event details</li>
                  </ol>
                  </div>
                  {error && <div className="error-message"><p><strong>Error:</strong> {error}</p></div>}
                  
                  {/* Show view existing application button if user has already applied */}
                  {hasApplied && existingApplicationId && (
                    <div className={`already-applied-message ${applicationStatus ? applicationStatus : ''}`}>
                      <h3>
                        {applicationStatus === 'approved' ? 'Application Approved!' :
                         applicationStatus === 'pending' ? 'Application Under Review' :
                         applicationStatus === 'rejected' ? 'Application Rejected' :
                         'Application Status'}
                      </h3>
                      <p>
                        {applicationStatus === 'approved' ? 'Congratulations! Your application has been approved.' :
                         applicationStatus === 'pending' ? 'Your application is currently under review by our team.' :
                         applicationStatus === 'rejected' ? 'Unfortunately, your application was not approved.' :
                         'You have already applied for this event.'}
                      </p>
                      <button 
                        onClick={handleViewExistingApplication} 
                        className={`status-button ${applicationStatus ? applicationStatus : ''}`}
                      >
                        View Your Application
                      </button>
                    </div>
                  )}
                </div>                {/* Only show the application form if user hasn't applied already */}
                {!hasApplied && (
                  <div className="application-form-container">
                    <h2>Application Form</h2>
                    
                    {/* Show verification banner for unverified users */}
                    {currentUser && !isEmailVerified && (
                      <div className="verification-banner">
                        <div className="verification-banner-content">
                          <FontAwesomeIcon icon={faExclamationTriangle} className="verification-banner-icon" />
                          <div className="verification-banner-text">
                            <h4>Email Verification Required</h4>
                            <p>You need to verify your email address before you can apply for events.</p>
                          </div>
                        </div>
                        <button 
                          onClick={() => setShowVerificationModal(true)}
                          className="verification-banner-button"
                          disabled={verificationSending || verificationCooldown > 0}
                        >
                          {verificationSending ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} spin className="icon-left" /> Sending...
                            </>
                          ) : verificationCooldown > 0 ? (
                            `Resend in ${verificationCooldown}s`
                          ) : (
                            'Verify Email'
                          )}
                        </button>
                      </div>
                    )}
                    
                    <ApplicationForm 
                      onSubmit={handleSubmit} 
                      loading={loading || checkingApplication} 
                      eventId={eventId} 
                      disabled={!isEmailVerified && currentUser !== null}
                    />
                    
                    {/* Verification Modal */}
                    {showVerificationModal && (
                      <VerificationModal
                        onSendVerification={handleSendVerificationEmail}
                        onClose={() => setShowVerificationModal(false)}
                        sending={verificationSending}
                        userEmail={currentUser?.email || undefined}
                      />
                    )}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </section>
    </>
  );
};

export default Apply;